import z from 'zod';
import { validatePriceList } from '../../features/price-lists/csv-validation';
import { numericString, optionalNumericString } from '../../lib/schemas/numeric-string';
import { requireFromNullable } from '../../lib/schemas/require-from-nullable';
import { slug } from '../../lib/schemas/slugs';
import { MAX_HANDLE_LEN } from './constants';

// Runs validation on the file to ensure it's a valid CSV
const priceListCsv = z
  .object({
    file: z.file(),
    id: z.string(),
  })
  .superRefine(async (file, ctx) => {
    const validationErrors = await validatePriceList(file.file);
    for (const error of validationErrors) {
      ctx.addIssue({
        code: 'custom',
        message: error,
        path: [],
      });
    }
  });

export const priceListBuilderSchema = z.object({
  name: z.string().nonempty(),
  handle: slug().max(MAX_HANDLE_LEN),
  currency: z.string(),

  automaticPricing: z
    .object({
      priceListSource: z.string().nonempty(),
      percentageVariation: z
        .object({
          amount: numericString({ min: 0, max: 100 }),
          direction: z.enum(['plus', 'minus']),
        })
        .optional(),
      exchangeRate: optionalNumericString({ min: 0 }),
    })
    .optional(),

  manualPricing: z
    .object({
      file: requireFromNullable(priceListCsv),
    })
    .optional(),
});

// Edit variant: handle is disabled in the UI and cannot be changed, so we skip
// slug-format validation. Handles created via the API may not conform to the
// slug regex, so we don't want to block on those
export const priceListEditBuilderSchema = priceListBuilderSchema.extend({
  handle: z.string(),
});

export type PriceListBuilderState = z.input<typeof priceListBuilderSchema>;
export type PriceListBuilderValidated = z.output<typeof priceListBuilderSchema>;
